import cx from 'classnames';
import { Ratio } from 'components/@tedui';
import { Link } from 'components/router';
import { Microcopy, Text } from 'components/typography';
import * as React from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import { Text as NewText } from 'components/@tedui';
import Image from 'components/image';
import { getDurationAriaLabel, getDurationString } from 'lib/duration-helpers';

export type TalkCardSizes = 'l' | 'm' | 's';

export type BadgeText = {
  small: string;
  large: string;
};

type TalkCardProps = {
  className?: string;
  href: string;
  imageUrl: string;
  title: string;
  duration: number;
  onClick?: () => void;
  publishedAt?: string;
  description?: string;
  presenterDisplayName?: string;
  size?: TalkCardSizes;
  numberOfLinesToClamp?: number;
  badgeText?: BadgeText;
};

const TalkCard = ({
  className = '',
  href,
  imageUrl,
  description,
  title,
  publishedAt,
  duration,
  presenterDisplayName,
  size = 's',
  numberOfLinesToClamp = 0,
  badgeText,
  onClick,
  ...props
}: TalkCardProps): React.ReactNode => {
  const { locale } = useIntl();

  const isLargeCard = size === 'l';
  const isMediumCard = size === 'm';
  const isSmallCard = size === 's';

  const presenterClassName = cx({
    'mb-2': isLargeCard || isMediumCard,
    'mb-1': isSmallCard
  });

  const textWrapperClassName = cx({
    'pr-4 pt-5 xl:pr-6 xl:pt-6': isLargeCard,
    'pt-3 md:pr-6 md:pt-4 xl:pt-6': isMediumCard,
    'pr-2 pt-3 xl:pt-4': isSmallCard
  });

  const descriptionClassName = cx('text-gray-900', {
    'mt-4': isLargeCard,
    'hidden md:mt-4 md:block': isMediumCard,
    'hidden md:mt-3 md:block xl:mt-4': isSmallCard
  });

  const publishedAtClassName = cx('text-gray-500', {
    'mt-2': isLargeCard,
    'mt-4 md:mt-5': isMediumCard,
    'mt-3 xl:mt-4': isSmallCard && !description,
    'mt-4': isSmallCard && description
  });

  const lineClampStyle =
    numberOfLinesToClamp && isMediumCard
      ? {
          style: {
            display: '-webkit-box',
            WebkitLineClamp: numberOfLinesToClamp,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }
        }
      : {};

  return (
    <Link
      href={href}
      className={`group block ${className}`}
      {...props}
      onClick={onClick}
    >
      <Ratio isAriaHidden>
        <Image
          src={imageUrl}
          alt={title}
          width="512"
          height="288"
          className="card-image size-full bg-gray-300 object-contain object-center transition-opacity"
        />
        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black opacity-50 transition-opacity group-hover:opacity-0" />
        {badgeText?.small && (
          <Microcopy
            size="xs"
            className="absolute bottom-3 left-3 rounded-xs bg-white pb-05 pl-1 pr-1 pt-05 uppercase"
          >
            {badgeText.small}
          </Microcopy>
        )}
        <Microcopy
          size="xs"
          className="absolute bottom-3 right-3 rounded-xs bg-black bg-opacity-70 pb-05 pl-1 pr-1 pt-05 font-bold text-white"
        >
          {getDurationString(duration)}
        </Microcopy>
      </Ratio>
      <div className={textWrapperClassName}>
        {presenterDisplayName && (
          <NewText
            useNewTextStyles
            variant="caption1"
            tag="p"
            UNSAFE_className={presenterClassName}
          >
            {presenterDisplayName}
          </NewText>
        )}
        {isLargeCard ? (
          // Special heading for large variant, so the title is visibly acceptable
          <NewText
            useNewTextStyles
            weight="font-bold"
            variant="header4"
            tag="p"
            UNSAFE_className="transition-colors group-hover:text-gray-700"
          >
            {title}
          </NewText>
        ) : (
          <NewText
            useNewTextStyles
            weight="font-bold"
            variant="subheader2"
            tag="p"
            UNSAFE_className="transition-colors group-hover:text-gray-700"
          >
            {title}
          </NewText>
        )}

        {/* Per requirement, for screenreaders duration should be read right after the title */}
        <div className="sr-only">{getDurationAriaLabel(duration, locale)}</div>
        {description && (
          <div className={descriptionClassName}>
            <Text size="m" {...lineClampStyle}>
              {description}
            </Text>
          </div>
        )}
        {publishedAt && (
          <Text size="s" className={publishedAtClassName}>
            <FormattedMessage defaultMessage="Posted" />
            {` `}
            <FormattedDate
              value={new Date(publishedAt)}
              year="numeric"
              month="short"
            />
          </Text>
        )}
      </div>
    </Link>
  );
};

export default TalkCard;
